import type { FC, ReactNode } from 'react'

import { ArrowRight } from 'lucide-react'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { SkyPanel } from '@/components/generals/sky-panel'
import { LogoMark } from '@/components/generals/logo'
import { ButtonLink } from '@/components/generals/button'
import { Foliage } from '@/components/landing/foliage'
import { PlanGrid } from '@/components/pricing/plan-grid'
import { PricingFaq } from '@/components/pricing/pricing-faq'

import { DEMO_HREF } from '@/constants/navigation'

const Pricing: FC = (): ReactNode => {
    return (
        <Layout>
            <Seo
                title='Pricing'
                description='Simple monthly plans for agents, teams and brokerages. Every Closewell plan starts with a guided pilot on one of your live deals.'
            />

            <div className='relative'>
                <Foliage side='left' className='top-[8rem] -left-[calc(min(30vw,460px)*0.05)] w-[min(30vw,460px)]' />

                <section aria-labelledby='pricing-title' className='relative z-20 px-5 pt-14 pb-14 text-center sm:pt-20 sm:pb-16'>
                    <h1 id='pricing-title' className='animate-in font-heading text-[clamp(3.2rem,7vw,5.75rem)] leading-[0.95] tracking-[-0.03em] text-foreground duration-1000 ease-[cubic-bezier(0.22,1,0.36,1)] fade-in blur-in-8 slide-in-from-bottom-8'>
                        Simple plans,
                        <span className='block italic'>no surprises</span>
                    </h1>

                    <p className='mx-auto mt-6 max-w-[34rem] animate-in text-[17px] leading-[1.5] text-muted-foreground delay-300 duration-1000 fill-mode-both fade-in slide-in-from-bottom-3 sm:text-[19px]'>
                        Pick the plan that matches how many deals you have open. Every plan starts with a guided pilot on one of your live transactions.
                    </p>
                </section>

                <section aria-label='Plans' className='relative px-2 sm:px-4 lg:px-5'>
                    <SkyPanel preload innerClassName='mx-auto max-w-6xl px-4 py-12 sm:px-8 sm:py-20'>
                        <PlanGrid />

                        <p className='mt-8 text-center text-sm text-foreground/65'>
                            Prices in US dollars, billed monthly. Cancel any time, and keep every document you stored.
                        </p>
                    </SkyPanel>
                </section>
            </div>

            <PricingFaq />

            <section aria-labelledby='pricing-cta-title' className='px-5 pt-28 pb-4 text-center sm:pt-36'>
                <div className='reveal'>
                    <LogoMark draw className='mx-auto size-11 text-foreground' />
                </div>

                <h2 id='pricing-cta-title' className='reveal reveal-d1 mt-6 font-heading text-[clamp(2.6rem,5vw,4.25rem)] leading-[0.98] tracking-[-0.03em] text-foreground'>
                    Not sure which plan fits?
                </h2>

                <p className='reveal reveal-d2 mx-auto mt-5 max-w-[32rem] text-[17px] leading-[1.55] text-muted-foreground'>
                    Walk us through how your deals run today. We will tell you honestly which plan makes sense, even if it is the smallest one.
                </p>

                <div className='reveal reveal-d3 mt-9 flex justify-center'>
                    <ButtonLink href={DEMO_HREF} size='lg'>
                        Book a walkthrough
                        <ArrowRight className='size-4 transition-transform duration-300 group-hover:translate-x-1' />
                    </ButtonLink>
                </div>
            </section>
        </Layout>
    )
}

export default Pricing
